import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import axios from 'axios';
import Breadcrumbs from '../components/Breadcrumbs';

const PodsExplorerPage = () => {
    const { selectedAKSCluster } = useSelector((state: RootState) => state.monitoring);
    const [pods, setPods] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [filter, setFilter] = useState<string>('');
    const { clusterName, namespace } = useParams<{ clusterName?: string; namespace?: string }>();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchPods = async () => {
            try {
                setLoading(true);
                // API endpoint should return all pods across all clusters/namespaces
                const response = await axios.get('/api/monitoring/pods');
                setPods(response.data.data);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching pods:', error);
                setLoading(false);
            }
        };
        fetchPods();
    }, []);

    // Filter by selected cluster/namespace if present
    const filteredPods = pods.filter((pod: any) =>
        (!clusterName || pod.cluster === clusterName) &&
        (!namespace || pod.namespace === namespace)
    );

    // Apply text filter on top of cluster/namespace filter
    const filteredPodsByText = filteredPods.filter((pod: any) =>
        pod.name.toLowerCase().includes(filter.toLowerCase()) ||
        pod.namespace.toLowerCase().includes(filter.toLowerCase()) ||
        pod.cluster.toLowerCase().includes(filter.toLowerCase()) ||
        (pod.status?.toLowerCase() || '').includes(filter.toLowerCase())
    );

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-400">Loading pods...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <Breadcrumbs />
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Kubernetes Pods</h1>
                    {selectedAKSCluster && (
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Filtered by: {selectedAKSCluster.name}
                        </p>
                    )}
                </div>
                {selectedAKSCluster && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        Showing {filteredPodsByText.length} of {pods.length} total pods
                    </div>
                )}
            </div>

            <div className="mb-6">
                <input
                    type="text"
                    placeholder="Filter by name, namespace, cluster or status..."
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                />
            </div>

            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Cluster</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Namespace</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Node</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Restarts</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Age</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredPodsByText.map((pod: any, index: number) => (
                            <tr
                                key={`${pod.cluster}-${pod.namespace}-${pod.name}-${index}`}
                                className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                onClick={() => navigate(`/kubernetes/pods/${pod.cluster}/${pod.namespace}/${pod.name}`)}
                            >
                                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-white">{pod.name}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <Link
                                        to={`/kubernetes/clusters/${pod.cluster}`}
                                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                        onClick={e => e.stopPropagation()}
                                    >
                                        {pod.cluster}
                                    </Link>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{pod.namespace}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${pod.status === 'Running'
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                        : pod.status === 'Pending'
                                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                        }`}>
                                        {pod.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{pod.nodeName || '�'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{pod.restartCount ?? '�'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{pod.age || '�'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredPodsByText.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        {filter ? 'No pods match your filter' :
                            selectedAKSCluster ? `No pods found in ${selectedAKSCluster.name}` :
                                'No pods found'}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PodsExplorerPage;
